import { v4 as uuidv4 } from 'uuid'

export const STORAGE_KEYS = {
  URL: 'udv-gateway-url',
  TOKEN: 'udv-gateway-token',
  USERNAME: 'udv-gateway-username',
  DEVICE_ID: 'udv-device-id',
}

/**
 * @param {string} url
 * @returns {string}
 */
export function normalizeServerUrl(url) {
  const trimmed = String(url == null ? '' : url).trim().replace(/\/+$/, '')
  if (!trimmed) {
    throw new Error('Gateway Server URL vacía')
  }
  return trimmed
}

/**
 * UUID v4 persistente en localStorage. No se borra al cerrar sesión.
 * @returns {string}
 */
export function getOrCreateDeviceId() {
  try {
    const existing = window.localStorage.getItem(STORAGE_KEYS.DEVICE_ID)
    if (existing) return existing
    const id = uuidv4()
    window.localStorage.setItem(STORAGE_KEYS.DEVICE_ID, id)
    return id
  } catch (e) {
    return uuidv4()
  }
}

/**
 * @param {{ serverUrl: string, token: string, username?: string }} session
 */
export function saveSession(session) {
  const serverUrl = normalizeServerUrl(session && session.serverUrl)
  const token = session && session.token ? String(session.token) : ''
  if (!token) {
    throw new Error('Token JWT ausente')
  }
  window.localStorage.setItem(STORAGE_KEYS.URL, serverUrl)
  window.localStorage.setItem(STORAGE_KEYS.TOKEN, token)
  if (session.username) {
    window.localStorage.setItem(STORAGE_KEYS.USERNAME, String(session.username))
  }
  getOrCreateDeviceId()
}

/**
 * @returns {{ serverUrl: string, token: string, username: string, deviceId: string } | null}
 */
export function loadSession() {
  try {
    const serverUrl = window.localStorage.getItem(STORAGE_KEYS.URL)
    const token = window.localStorage.getItem(STORAGE_KEYS.TOKEN)
    const username = window.localStorage.getItem(STORAGE_KEYS.USERNAME) || ''
    if (!serverUrl || !token) return null
    return {
      serverUrl,
      token,
      username,
      deviceId: getOrCreateDeviceId(),
    }
  } catch (e) {
    return null
  }
}

/** Cierra sesión JWT. Conserva DeviceID. */
export function clearSession() {
  try {
    window.localStorage.removeItem(STORAGE_KEYS.URL)
    window.localStorage.removeItem(STORAGE_KEYS.TOKEN)
    window.localStorage.removeItem(STORAGE_KEYS.USERNAME)
  } catch (e) {
    /* ignore */
  }
}

/**
 * @param {{ token?: string } | null} [session]
 * @returns {{ Authorization?: string }}
 */
export function authHeaders(session) {
  const s = session || loadSession()
  if (!s || !s.token) return {}
  return { Authorization: `Bearer ${s.token}` }
}

/**
 * @param {object} body
 * @returns {string|null}
 */
export function extractToken(body) {
  if (!body || typeof body !== 'object') return null
  return body.token || body.access_token || body.jwt || null
}

/**
 * @param {object|string|null} body
 * @returns {string|null}
 */
export function extractVapidKey(body) {
  if (body == null) return null
  if (typeof body === 'string') {
    const trimmed = body.trim()
    return trimmed || null
  }
  if (typeof body !== 'object') return null
  return (
    body.vapid_public_key ||
    body.vapidPublicKey ||
    body.public_key ||
    body.publicKey ||
    body.key ||
    null
  )
}

const HEALTH_TIMEOUT_MS = 4000

/**
 * GET {serverUrl}/api/v1/health
 * @param {string} [serverUrl]
 * @returns {Promise<{ ok: boolean, reason?: string, status?: number }>}
 */
export async function checkHealth(serverUrl) {
  let url
  try {
    if (serverUrl) {
      url = normalizeServerUrl(serverUrl)
    } else {
      const session = loadSession()
      if (!session || !session.serverUrl) {
        return { ok: false, reason: 'no_url' }
      }
      url = session.serverUrl
    }
  } catch (e) {
    return { ok: false, reason: 'no_url' }
  }

  let controller = null
  let timer = null
  try {
    const opts = { method: 'GET' }
    if (typeof AbortController !== 'undefined') {
      controller = new AbortController()
      opts.signal = controller.signal
      timer = setTimeout(function () {
        try {
          controller.abort()
        } catch (e) {
          /* ignore */
        }
      }, HEALTH_TIMEOUT_MS)
    }
    const res = await fetch(`${url}/api/v1/health`, opts)
    if (!res.ok) {
      return { ok: false, reason: 'http', status: res.status }
    }
    return { ok: true, status: res.status }
  } catch (e) {
    const aborted =
      e &&
      (e.name === 'AbortError' ||
        (typeof e.message === 'string' && /abort/i.test(e.message)))
    return { ok: false, reason: aborted ? 'timeout' : 'network' }
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * POST {serverUrl}/api/v1/auth/login
 * @param {string} serverUrl
 * @param {string} username
 * @param {string} password
 */
export async function login(serverUrl, username, password) {
  const url = normalizeServerUrl(serverUrl)
  const user = String(username || '').trim()
  if (!user) {
    throw new Error('Usuario vacío')
  }
  if (password == null || String(password) === '') {
    throw new Error('Contraseña vacía')
  }

  let res
  try {
    res = await fetch(`${url}/api/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: user, password: String(password) }),
    })
  } catch (e) {
    throw new Error(
      `No se pudo conectar al Gateway: ${e && e.message ? e.message : e}`
    )
  }

  if (res.status === 401) {
    throw new Error('Usuario o contraseña incorrectos')
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(
      `Login falló (HTTP ${res.status})${text ? `: ${text.slice(0, 200)}` : ''}`
    )
  }

  let data
  try {
    data = await res.json()
  } catch (e) {
    throw new Error('El Gateway no devolvió JSON en el login')
  }

  const token = extractToken(data)
  if (!token) {
    throw new Error('El Gateway no devolvió un token JWT')
  }

  saveSession({ serverUrl: url, token, username: user })
  return {
    serverUrl: url,
    token,
    username: user,
    deviceId: getOrCreateDeviceId(),
    vapidPublicKey: extractVapidKey(data),
    raw: data,
  }
}
